import {
  doc,
  DocumentReference,
  type FirestoreDataConverter,
} from 'firebase/firestore';
import type {CourseId} from 'types/common';
import {getCourseDocumentRef} from '@cip/core/firebase/client';
import {validateOptionalPrimitiveField} from 'utils/general';
import {
  FormInfo,
  validateFormInfo,
} from 'course/application/firebase/models/formData';
import {
  validateAboutSections,
  AboutSections,
  AboutSectionItemTypes,
  SECTION_TYPES,
} from 'course/application/firebase/models/cardData';
import {
  DebugInfo,
  validateDebugInfo,
} from 'course/application/firebase/models/debugData';

// should be one of ALL_FIREBASE_ROLES
export type ApplicationType = 'sl' | 'student';

export const SUPPORTED_SECTION_LEAD_TODO_TYPES = [
  'aboutJob',
  'form',
  // `lesson` lets a section-leader application include a slide-based lesson step
  // (e.g. the CIPX "Learn about Code in Place X" application lesson), the same
  // mechanism the student application uses. Like the student side, a `lesson`
  // item must carry an explicit `to` (it is not in TYPES_WITH_DEFAULT_TO_LINK).
  'lesson',
  'record',
  'code',
  'debugging',
  'simteaching',
  'sectionteaching',
  // PAI-parented courses run the section-teaching practice on the SLIDES
  // surface (the `mathteachable` route) instead of the shared IDE exercise
  // every other course uses. A distinct type keeps that routing explicit in
  // the data instead of inferred from the course's parent (see
  // `getTodoItemRoute`'s `sectionteaching-pai` case).
  'sectionteaching-pai',
] as const;
type SectionLeadTodoType = (typeof SUPPORTED_SECTION_LEAD_TODO_TYPES)[number];

export const SUPPORTED_STUDENT_TODO_TYPES = [
  'aboutCourse',
  'form',
  'lesson',
  'code',
] as const;
type StudentTodoType = (typeof SUPPORTED_STUDENT_TODO_TYPES)[number];

export type TodoType = SectionLeadTodoType | StudentTodoType;

/**
 * Closed set of per-item icon overrides offered by the application editor's
 * icon picker (see `SplashTodoEditor`). Kept as an explicit list rather than
 * a free-text icon name so the editor can only ever select an icon that is
 * actually wired up to a component — see `TODO_ICON_OVERRIDE_ICONS` in
 * `CipxTodoList.tsx`.
 */
export const SUPPORTED_TODO_ICON_OVERRIDES = [
  'play',
  'playCircle',
  'video',
  'book',
  'bookOpen',
  'graduationCap',
  'lightbulb',
  'compass',
  'mapSigns',
  'comments',
  'commentDots',
  'users',
  'bullhorn',
  'rocket',
  'pen',
  'pencilAlt',
  'chalkboardTeacher',
  'bug',
  'code',
  'python',
  'flagCheckered',
  'star',
  'trophy',
  'checkCircle',
  'clipboardList',
  'questionCircle',
  'heart',
  'globe',
  'signature',
  'smile',
  'feather',
  'seedling',
  'leaf',
  'tree',
  'planet',
  'language',
] as const;
export type TodoIconOverride = (typeof SUPPORTED_TODO_ICON_OVERRIDES)[number];

interface BaseTodoItem {
  id: string;
  title: string;
  to?: string; // Optional for todo types that have hardcoded routes
  optional?: boolean; // Optional for todo types that can be optional
  mobileTitle?: string;
  /**
   * The `id` of another todo item that must be complete before this one unlocks.
   * When set and the prerequisite is incomplete, the CIPX timeline renders this
   * item locked. Omitted → never locked.
   */
  lockedUntilComplete?: string;
  /**
   * Overrides the badge icon this item renders with, in place of its type's
   * default (see `getCipxIcon`/`getIcon`). This lives on the individual
   * Firestore todo item, so setting it on one course's item never affects
   * any other course's items of the same type. Omitted → type's default icon.
   */
  icon?: TodoIconOverride;
}

export const TYPES_WITH_DEFAULT_TO_LINK: readonly TodoType[] = [
  'aboutCourse',
  'aboutJob',
  'form',
  'record',
  'debugging',
  'simteaching',
  'sectionteaching',
  'sectionteaching-pai',
] as const;

export const TYPES_WITH_OPTIONAL_CAPABILITY: readonly TodoType[] = [
  'code',
  'lesson',
];

export interface SectionLeadTodoItem extends BaseTodoItem {
  type: SectionLeadTodoType;
}

export interface StudentTodoItem extends BaseTodoItem {
  type: StudentTodoType;
}

export interface ApplicationCard<
  T extends SectionLeadTodoItem | StudentTodoItem,
> {
  id: string;
  title: string;
  todo: T[];
}

export type AnyApplicationCardType =
  | ApplicationCard<SectionLeadTodoItem>
  | ApplicationCard<StudentTodoItem>;

export type ApplicationTodoItem = SectionLeadTodoItem | StudentTodoItem;

/**
 * Whether a todo item is complete enough to publish: it has a non-empty
 * title and, for types without a hardcoded default route
 * ({@link TYPES_WITH_DEFAULT_TO_LINK}), a non-empty `to` link. Shared by the
 * splash-timeline editor's per-row indicator (`SplashTodoEditor`) and its
 * autosave gate (`ApplicationSplashPageEditor`) so a row never shows
 * "incomplete" while autosave has already published it, or vice versa.
 *
 * @param item - The todo item to check.
 * @returns Whether the item is complete.
 */
export function isTodoItemComplete(item: ApplicationTodoItem): boolean {
  return (
    !!item.title.trim() &&
    (TYPES_WITH_DEFAULT_TO_LINK.includes(item.type) || !!item.to?.trim())
  );
}

/**
 * Admin-editable persona bio for ONE simulated section student, keyed by the
 * seat's student id in {@link SimSectionConfig.personas}. Only the prose bio
 * fields are editable — a seat's name, voice, and behavior tuning are fixed
 * (names are baked into the backend's cached router prompts). Every field is
 * optional; an absent or empty field keeps the backend profile's own value
 * (the canonical bios in `src/apps/simteach/app/section_teaching/personas.py`
 * for CIPX-like courses, blank for PAI).
 */
export interface SimSectionPersonaBio {
  /** What they studied / are studying and their subject background. */
  academicBackground?: string;
  /** Their broad goals for taking the course. */
  goals?: string;
  /** How they engage in class (reactions, question habits). */
  engagementStyle?: string;
  /** How they prefer to learn. */
  learningStyle?: string;
  /** Diction and cadence of their speech. */
  speechPattern?: string;
  /** How they'd describe themselves out loud. */
  selfDescription?: string;
  /** How they privately view themselves. */
  privateSelfDescription?: string;
}

/** The persona bio fields, in display order (mirrors the backend's list). */
export const SIM_SECTION_PERSONA_BIO_FIELDS = [
  'academicBackground',
  'goals',
  'engagementStyle',
  'learningStyle',
  'speechPattern',
  'selfDescription',
  'privateSelfDescription',
] as const;

/**
 * One admin-authored closing question for the sim section, plus the tutoring
 * metadata that steers the exchange after it is asked. Only `id` and `text`
 * are required. Unlike the sibling `*_override` fields these are read
 * SERVER-SIDE by the simteach backend, never from the client payload — a
 * client-supplied bank would defeat the anti-gaming design. They are also
 * stored apart from the rest of the sim-section config; see
 * {@link SimSectionPrivateConfig}.
 */
export interface SimSectionQuestion {
  /**
   * Stable `/`-free identifier, generated once in the editor and kept across
   * text edits — the repeat-avoidance history and the attempt doc's
   * `questionId` key on it.
   */
  id: string;
  /** The question, exactly as the simulated student should ask it. */
  text: string;
  /** The correct idea the applicant's explanation should land. */
  underlyingConcept?: string;
  /** The wrong belief the simulated student starts with. */
  initialMisconception?: string;
  /** What an explanation must do for the student to count it resolved. */
  successCriteria?: string[];
}

/**
 * Admin-editable configuration for the sim-section (Practice Teaching)
 * experience, edited in the application editor's Sim Section tab. Every field
 * is optional: an absent or empty field keeps the hard-coded default (the
 * backend's course profile in
 * `src/apps/simteach/app/section_teaching/course_profiles.py`, and the
 * scenario's deck in `course/simteach/scenarioConfig.ts`), so a course with no
 * config saved behaves exactly as before this field existed.
 */
export interface SimSectionConfig {
  /**
   * Short description of the course the simulated students are in, spliced
   * into their persona prompts (e.g. "an introductory programming class").
   * Omitted → the backend profile's default course description.
   */
  aboutCourse?: string;

  /**
   * Prose description of what the simulated students already know, injected
   * into their generation prompts so replies stay inside the course's actual
   * progress. Omitted → the backend profile's default knowledge block.
   */
  studentKnowledge?: string;

  /**
   * Id of the slide deck (`slides/{courseId}/decks/{deckId}`) applicants
   * present during the sim section. Omitted → the scenario's built-in deck.
   */
  deckId?: string;

  /**
   * Per-seat persona bio overrides, keyed by the seat's student id (`'1'` |
   * `'2'` | `'3'`). Omitted seats/fields keep the backend profile's bios.
   */
  personas?: Record<string, SimSectionPersonaBio>;
}

/**
 * Sim-section config that applicants must NOT be able to read, stored in the
 * admin-only `private` subcollection under the application-data doc rather
 * than as fields on it — that doc is world-readable, and everything here is
 * something an applicant could otherwise game. Read server-side by the
 * simteach backend (`section_teaching/admin_questions.py`).
 */
export interface SimSectionPrivateConfig {
  /**
   * The closing-question bank for this course's sim section. Non-empty
   * REPLACES the backend's built-in conceptual bank; omitted or empty keeps
   * it. Keep the list larger than the attempt cap so the no-repeat rotation
   * never runs dry.
   */
  questions?: SimSectionQuestion[];
}

export interface SectionLeadApplicationData {
  /**
   * Todo item cards for application. These are displayed on the splash page.
   */
  splashCards: ApplicationCard<SectionLeadTodoItem>[];

  /**
   * Debug problem information for debugging section of application.
   */
  debugInfo: DebugInfo;

  /**
   * Form field information for application form.
   */
  formInfo: FormInfo;

  /**
   * Item information for about job section of application.
   */
  aboutSections: AboutSections;

  /**
   * Image url for custom application background image. Ignored when
   * `backgroundColor` is set.
   */
  url?: string;

  /**
   * Solid background color for the splash page, as a CSS color string (e.g.
   * `#f5f5f7`). A course uses either a solid color or a background image: when
   * this is set it wins, and `url` is ignored. The Application Editor offers
   * the two as an either/or choice and clears this field when switching back to
   * an image.
   */
  backgroundColor?: string;

  /**
   * Sim-section (Practice Teaching) configuration for this course. Omitted
   * when the course has never saved any.
   */
  simSection?: SimSectionConfig;

  /**
   * Info Page categories (from {@link AboutSectionItemTypes}, e.g.
   * `'carousel'`) hidden for this course, in both the Info Page editor's
   * expanded view and the public Explore page — regardless of whether the
   * category already holds items. Omitted or empty → every category with
   * items is shown, the pre-existing behavior.
   */
  disabledSectionTypes?: AboutSectionItemTypes[];
}

export interface StudentApplicationData {
  /**
   * Todo item cards for application. These are displayed on the splash page.
   */
  splashCards: ApplicationCard<StudentTodoItem>[];

  /**
   * Form field information for application form.
   */
  formInfo: FormInfo;

  /**
   * Item information for about course section of application.
   */
  aboutSections: AboutSections;

  /**
   * Image url for custom application background image. Ignored when
   * `backgroundColor` is set.
   */
  url?: string;

  /**
   * Solid background color for the splash page, as a CSS color string (e.g.
   * `#f5f5f7`). A course uses either a solid color or a background image: when
   * this is set it wins, and `url` is ignored. The Application Editor offers
   * the two as an either/or choice and clears this field when switching back to
   * an image.
   */
  backgroundColor?: string;

  /**
   * Info Page categories (from {@link AboutSectionItemTypes}, e.g.
   * `'carousel'`) hidden for this course, in both the Info Page editor's
   * expanded view and the public Explore page — regardless of whether the
   * category already holds items. Omitted or empty → every category with
   * items is shown, the pre-existing behavior.
   */
  disabledSectionTypes?: AboutSectionItemTypes[];
}
export type ApplicationData =
  | SectionLeadApplicationData
  | StudentApplicationData;

/**
 * Maps application types to their corresponding data types.
 */
type ApplicationDataTypeMap = {
  sl: SectionLeadApplicationData;
  student: StudentApplicationData;
};

/**
 * Validates one raw persona-bio map entry, keeping only the known bio fields
 * that hold non-empty strings.
 *
 * @param data - The raw per-seat value from Firestore.
 * @returns The validated bio, or `undefined` when nothing valid remains.
 */
const validateSimSectionPersonaBio = (
  data: unknown,
): SimSectionPersonaBio | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const raw: Record<string, unknown> = {...data};
  const result: SimSectionPersonaBio = {};
  for (const field of SIM_SECTION_PERSONA_BIO_FIELDS) {
    const value = raw[field];
    if (typeof value === 'string' && value.trim().length > 0) {
      result[field] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

/**
 * Validates one raw closing-question entry: `id` (non-empty, `/`-free) and
 * `text` are required; metadata is kept only when well-formed. Mirrors the
 * backend's `admin_questions.py` normalization — keep the two in step.
 *
 * @param data - One raw array entry from Firestore.
 * @returns The validated question, or `undefined` to drop the entry.
 */
const validateSimSectionQuestion = (
  data: unknown,
): SimSectionQuestion | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const raw: Record<string, unknown> = {...data};
  if (
    typeof raw.id !== 'string' ||
    raw.id.trim().length === 0 ||
    raw.id.includes('/') ||
    typeof raw.text !== 'string' ||
    raw.text.trim().length === 0
  ) {
    return undefined;
  }
  const result: SimSectionQuestion = {id: raw.id.trim(), text: raw.text.trim()};
  if (
    typeof raw.underlyingConcept === 'string' &&
    raw.underlyingConcept.trim().length > 0
  ) {
    result.underlyingConcept = raw.underlyingConcept.trim();
  }
  if (
    typeof raw.initialMisconception === 'string' &&
    raw.initialMisconception.trim().length > 0
  ) {
    result.initialMisconception = raw.initialMisconception.trim();
  }
  if (Array.isArray(raw.successCriteria)) {
    const criteria = raw.successCriteria.filter(
      (entry): entry is string =>
        typeof entry === 'string' && entry.trim().length > 0,
    );
    if (criteria.length > 0) {
      result.successCriteria = criteria.map(entry => entry.trim());
    }
  }
  return result;
};

/**
 * Validates the raw `simSection` field of an application-data doc, keeping
 * only the known keys that hold non-empty strings (matching the leniency of
 * the sibling validators: malformed entries are dropped, never thrown on).
 *
 * @param data - The raw field value from Firestore.
 * @returns The validated config, or `undefined` when nothing valid remains
 *     (so an empty map reads the same as an absent field).
 */
const validateSimSection = (data: unknown): SimSectionConfig | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const raw: Record<string, unknown> = {...data};
  const result: SimSectionConfig = {};
  const keys = ['aboutCourse', 'studentKnowledge', 'deckId'] as const;
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      result[key] = value;
    }
  }
  if (raw.personas && typeof raw.personas === 'object') {
    const personas: Record<string, SimSectionPersonaBio> = {};
    for (const [seatId, rawBio] of Object.entries(raw.personas)) {
      const bio = validateSimSectionPersonaBio(rawBio);
      if (bio !== undefined) {
        personas[seatId] = bio;
      }
    }
    if (Object.keys(personas).length > 0) {
      result.personas = personas;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

/**
 * Validates the raw admin-only sim-section doc. Same leniency as the sibling
 * validators: malformed question entries are dropped rather than thrown on,
 * and duplicate ids keep the first entry.
 *
 * Exported for its unit tests, which pin the `questions` contract against the
 * simteach backend's normalizer (see applicationData.test.ts and the simteach
 * tests/test_admin_questions.py).
 *
 * @param data - The raw document data from Firestore.
 * @returns The validated config, or `undefined` when nothing valid remains
 *     (so an empty doc reads the same as a missing one).
 */
export const validateSimSectionPrivate = (
  data: unknown,
): SimSectionPrivateConfig | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const raw: Record<string, unknown> = {...data};
  if (!Array.isArray(raw.questions)) {
    return undefined;
  }
  const seenIds = new Set<string>();
  const questions: SimSectionQuestion[] = [];
  for (const rawQuestion of raw.questions) {
    const question = validateSimSectionQuestion(rawQuestion);
    if (question !== undefined && !seenIds.has(question.id)) {
      seenIds.add(question.id);
      questions.push(question);
    }
  }
  return questions.length > 0 ? {questions} : undefined;
};

/**
 * Validates the raw `disabledSectionTypes` field of an application-data doc,
 * keeping only entries that are one of {@link SECTION_TYPES} and dropping
 * duplicates. Mirrors the leniency of the sibling validators: malformed
 * entries are dropped rather than thrown on.
 *
 * @param data - The raw field value from Firestore.
 * @returns The validated list, or `undefined` when nothing valid remains (so
 *     an empty/absent field reads the same as no disabled categories).
 */
const validateDisabledSectionTypes = (
  data: unknown,
): AboutSectionItemTypes[] | undefined => {
  if (!Array.isArray(data)) {
    return undefined;
  }
  const sectionTypes: readonly string[] = SECTION_TYPES;
  const result = [
    ...new Set(
      data.filter(
        (value): value is AboutSectionItemTypes =>
          typeof value === 'string' && sectionTypes.includes(value),
      ),
    ),
  ];
  return result.length > 0 ? result : undefined;
};

const SectionLeadAppDataConverter: FirestoreDataConverter<ApplicationData> = {
  toFirestore: (data: ApplicationData) => data,
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options);

    if (!data || typeof data !== 'object') {
      return {
        splashCards: [],
        formInfo: {title: '', type: '', fields: []},
        aboutSections: {} as AboutSections,
        debugInfo: {} as DebugInfo,
      } as SectionLeadApplicationData;
    }

    // Validate required fields
    const splashCards = validateCards(data.splashCards, SUPPORTED_SECTION_LEAD_TODO_TYPES);
    const formInfo = validateFormInfo(data.formInfo);
    const aboutSections = validateAboutSections(data.aboutSections);
    const debugInfo = validateDebugInfo(data.debugInfo);

    const result: SectionLeadApplicationData = {
      splashCards,
      formInfo,
      aboutSections,
      debugInfo,
    };

    const url = data.url;
    const backgroundColor = data.backgroundColor;

    // Validate optional fields
    if (url !== undefined && typeof url === 'string' && url.trim().length > 0) {
      result.url = url;
    }
    if (
      backgroundColor !== undefined &&
      typeof backgroundColor === 'string' &&
      backgroundColor.trim().length > 0
    ) {
      result.backgroundColor = backgroundColor;
    }

    const simSection = validateSimSection(data.simSection);
    if (simSection !== undefined) {
      result.simSection = simSection;
    }

    const disabledSectionTypes = validateDisabledSectionTypes(
      data.disabledSectionTypes,
    );
    if (disabledSectionTypes !== undefined) {
      result.disabledSectionTypes = disabledSectionTypes;
    }

    return result;
  },
};

const StudentAppDataConverter: FirestoreDataConverter<ApplicationData> = {
  toFirestore: (data: ApplicationData) => data,
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options);

    if (!data || typeof data !== 'object') {
      return {
        splashCards: [],
        formInfo: {title: '', type: '', fields: []},
        aboutSections: {} as AboutSections,
      } as StudentApplicationData;
    }

    // Validate required fields
    const splashCards = validateCards(data.splashCards, SUPPORTED_STUDENT_TODO_TYPES);
    const formInfo = validateFormInfo(data.formInfo);
    const aboutSections = validateAboutSections(data.aboutSections);

    const result: StudentApplicationData = {
      splashCards,
      formInfo,
      aboutSections,
    };

    const url = data.url;
    const backgroundColor = data.backgroundColor;

    // Validate optional fields
    if (url !== undefined && typeof url === 'string' && url.trim().length > 0) {
      result.url = url;
    }
    if (
      backgroundColor !== undefined &&
      typeof backgroundColor === 'string' &&
      backgroundColor.trim().length > 0
    ) {
      result.backgroundColor = backgroundColor;
    }

    const disabledSectionTypes = validateDisabledSectionTypes(
      data.disabledSectionTypes,
    );
    if (disabledSectionTypes !== undefined) {
      result.disabledSectionTypes = disabledSectionTypes;
    }

    return result;
  },
};

/**
 * Gets a reference to the application data document for a specific application
 * type. The return type is automatically inferred based on the type parameter,
 * providing full type safety. See ApplicationDataTypeMap for the possible
 * types.
 *
 * @example
 * // Returns DocumentReference<SectionLeadApplicationData>
 * const slRef = getApplicationDataRef(courseId, 'sl');
 *
 * @example
 * // Returns DocumentReference<StudentApplicationData>
 * const studentRef = getApplicationDataRef(courseId, 'student');
 */
export function getApplicationDataRef<T extends ApplicationType>(
  courseId: CourseId,
  type: T,
): DocumentReference<ApplicationDataTypeMap[T]> {
  if (type === 'sl') {
    return doc(
      getCourseDocumentRef({courseId}),
      'docs/application-data/role',
      type,
    ).withConverter(SectionLeadAppDataConverter) as DocumentReference<
      ApplicationDataTypeMap[T]
    >;
  } else {
    return doc(
      getCourseDocumentRef({courseId}),
      'docs/application-data/role',
      type,
    ).withConverter(StudentAppDataConverter) as DocumentReference<
      ApplicationDataTypeMap[T]
    >;
  }
}

/**
 * Path segments of the admin-only sim-section doc, relative to the SL
 * application-data doc. Kept as one constant because the simteach backend
 * hard-codes the same tail (`section_teaching/admin_questions.py`) and the
 * `private` segment is what the admin-only rule matches on.
 */
const SIM_SECTION_PRIVATE_PATH = 'private/simSection';

/**
 * Gets a reference to a course's admin-only sim-section doc. Readable and
 * writable by course admins only, unlike the application-data doc it hangs
 * under — see {@link SimSectionPrivateConfig}.
 *
 * @param courseId - The course whose sim section is being configured.
 * @returns The typed document reference; the doc may not exist yet, which
 *     reads as an absent config.
 */
export function getSimSectionPrivateRef(
  courseId: CourseId,
): DocumentReference<SimSectionPrivateConfig> {
  return doc(
    getApplicationDataRef(courseId, 'sl'),
    SIM_SECTION_PRIVATE_PATH,
  ).withConverter(SimSectionPrivateConverter);
}

const SimSectionPrivateConverter: FirestoreDataConverter<SimSectionPrivateConfig> =
  {
    toFirestore: (data: SimSectionPrivateConfig) => data,
    fromFirestore: (snapshot, options) =>
      validateSimSectionPrivate(snapshot.data(options)) ?? {},
  };

// Validation functions
export const isBaseTodoItem = (object: unknown): object is BaseTodoItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const todo = object as BaseTodoItem;

  if (typeof todo.id !== 'string' || typeof todo.title !== 'string') {
    return false;
  }

  if (!validateOptionalPrimitiveField(todo.mobileTitle, 'string')) {
    return false;
  }

  if (!validateOptionalPrimitiveField(todo.lockedUntilComplete, 'string')) {
    return false;
  }

  if (!validateOptionalPrimitiveField(todo.icon, 'string')) {
    return false;
  }

  if (
    todo.icon !== undefined &&
    !SUPPORTED_TODO_ICON_OVERRIDES.includes(todo.icon as TodoIconOverride)
  ) {
    return false;
  }

  return true;
};

export const isTodoItem = (
  object: unknown,
  supportedTodoTypes: readonly TodoType[],
): object is StudentTodoItem => {
  if (!isBaseTodoItem(object)) {
    return false;
  }

  const todo = object as StudentTodoItem;

  if (typeof todo.type !== 'string') {
    return false;
  }

  if (!supportedTodoTypes.includes(todo.type as TodoType)) {
    return false;
  }

  if (
    // (TYPES_WITH_DEFAULT_TO_LINK.includes(todo.type) && todo.to) || // TODO: add this back when merged and data is cleaned
    !TYPES_WITH_DEFAULT_TO_LINK.includes(todo.type) &&
    typeof todo.to !== 'string'
  ) {
    return false;
  }

  return true;
};

export const isApplicationCard = <
  T extends SectionLeadTodoItem | StudentTodoItem,
>(
  object: unknown,
  supportedTodoTypes: readonly TodoType[],
): object is ApplicationCard<T> => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const card = object as ApplicationCard<T>;

  if (typeof card.title !== 'string' || !Array.isArray(card.todo)) {
    return false;
  }

  // Validate each todo item
  for (const todoItem of card.todo) {
    if (!isTodoItem(todoItem, supportedTodoTypes)) {
      return false;
    }
  }

  return true;
};

export const isStudentApplicationData = (
  object: unknown,
): object is StudentApplicationData => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const data = object as StudentApplicationData;
  if (!Array.isArray(data.splashCards)) {
    return false;
  }

  // Validate each card
  for (const card of data.splashCards) {
    if (!isApplicationCard(card, SUPPORTED_STUDENT_TODO_TYPES)) {
      return false;
    }
  }

  return true;
};

export const isSectionLeadApplicationData = (
  object: unknown,
): object is SectionLeadApplicationData => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const data = object as SectionLeadApplicationData;

  if (!Array.isArray(data.splashCards)) {
    return false;
  }

  // Validate each card
  for (const card of data.splashCards) {
    if (!isApplicationCard(card, SUPPORTED_SECTION_LEAD_TODO_TYPES)) {
      return false;
    }
  }

  return true;
};

const validateCards = (
  data: unknown,
  supportedTodoTypes: readonly TodoType[],
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
): ApplicationCard<any>[] => {
  if (!Array.isArray(data)) {
    console.warn('Cards data is not an array, returning empty array');
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const validatedCards: ApplicationCard<any>[] = [];

  for (const card of data) {
    if (isApplicationCard(card, supportedTodoTypes)) {
      validatedCards.push(card);
    } else {
      console.warn(
         
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        `Invalid card data (title: ${(card as any)?.title || 'unknown'}), skipping`,
      );
    }
  }

  return validatedCards;
};
